import { useEffect, useState, type FormEvent } from 'react'
import { Plus, Search, Trash2 } from 'lucide-react'
import { api } from '../../api/client'
import type { Contact } from '../../api/types'
import { EmptyState } from '../../components/EmptyState'
import { PageHeader } from '../../components/PageHeader'
import { useI18n } from '../../i18n'

export function ContactsPage() {
  const { t } = useI18n()
  const [items, setItems] = useState<Contact[]>([])
  const [query, setQuery] = useState('')
  const [error, setError] = useState('')
  const [showForm, setShowForm] = useState(false)

  useEffect(() => { api.get<Contact[]>('/contacts').then(setItems).catch((reason: Error) => setError(reason.message)) }, [])

  async function create(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    const form = new FormData(event.currentTarget)
    try {
      const item = await api.post<Contact>('/contacts', { name: form.get('name'), address: form.get('address') })
      setItems((current) => [...current, item])
      setShowForm(false)
      event.currentTarget.reset()
    } catch (reason) { setError(reason instanceof Error ? reason.message : 'Unable to add contact.') }
  }

  const visible = items.filter((item) => `${item.name} ${item.address}`.toLowerCase().includes(query.toLowerCase()))

  return <>
    <PageHeader title="user.contacts" description="user.contactsDescription" action={
      <button onClick={() => setShowForm((value) => !value)} className="btn-primary flex items-center gap-2"><Plus size={16}/>{t('user.addContact')}</button>
    } />

    {showForm && (
      <form onSubmit={create} className="panel mb-5 grid gap-3 p-4 sm:grid-cols-[1fr_1fr_auto]">
        <input className="field" name="name" placeholder={t('user.name')} required />
        <input className="field" name="address" type="email" placeholder={t('user.address')} required />
        <button className="btn-primary">{t('user.addContact')}</button>
      </form>
    )}

    <label className="relative mb-5 block max-w-sm">
      <Search className="absolute left-3 top-3 text-muted-foreground" size={16}/>
      <input value={query} onChange={(event) => setQuery(event.target.value)} className="field pl-9" aria-label={t('common.search')} placeholder={t('common.search')} />
    </label>

    {error && <p className="mb-4 rounded-lg bg-destructive/10 px-3 py-2 text-sm text-destructive">{error}</p>}

    {visible.length === 0 ? <EmptyState title="user.emptyContacts" /> : (
      <div className="panel divide-y overflow-hidden">
        {visible.map((item) => (
          <div key={item.id} className="flex items-center justify-between p-4 transition-colors hover:bg-muted/30">
            <div className="min-w-0">
              <p className="font-medium truncate">{item.name}</p>
              <p className="text-sm text-muted-foreground truncate">{item.address}</p>
            </div>
            <button className="shrink-0 rounded-lg p-2 text-destructive transition-colors hover:bg-destructive/10" aria-label="Delete contact" onClick={async () => { try { await api.delete(`/contacts/${item.id}`); setItems((current) => current.filter((contact) => contact.id !== item.id)) } catch (reason) { setError(reason instanceof Error ? reason.message : 'Unable to delete contact.') } }}>
              <Trash2 size={16}/>
            </button>
          </div>
        ))}
      </div>
    )}
  </>
}
